// `/admin/transactions` (`spec/17-frontend.md` §8.2). Foco: **a fila de cobranças**.
//
// Esta é a rota-PAI: ela desenha a lista e, por cima, o `RouteDrawer` com o `<Outlet/>`
// da rota `$id` (`spec/24` §1 decisão 5). Deep link em `.../txn_…` monta a lista embaixo
// e o drawer aberto; fechar volta para cá preservando o `search`.
//
// Só roteador: o schema de `search`, a navegação e o drawer. A tela é
// `features/admin-transactions/transactions-screen.tsx`.
//
// Os filtros vivem na query string (`spec/17` §3), não em `useState`: recarregar a
// página, mandar o link para outro operador e o botão de voltar continuam valendo. O
// schema mora aqui (e não no `index`) porque o `$id` é filho desta rota e herda o mesmo
// `search`, inclusive o `mode`, que decide em qual modo o detalhe é lido.

import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { rangeForPreset } from '../components/kit/date-range-filter.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { TRANSACTION_STATUSES } from '../components/kit/tx-status.tsx';
import { regionOf, regionSearchField, regionSearchOf } from '../features/admin-region/region.tsx';
import { useAdminTransactionDrawerHeader } from '../features/admin-transactions/transaction-detail.tsx';
import {
  type AdminFilterValue,
  AdminTransactionsScreen,
} from '../features/admin-transactions/transactions-screen.tsx';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** `encodeId` (`packages/domain/src/ids.ts`) é Crockford base32 **minúsculo**, 26 dígitos. */
const SELLER_ID = /^sel_[0-9a-hjkmnp-tv-z]{26}$/;

/**
 * Query string tolerante a lixo: `?status=banana` cai em `undefined` em vez de derrubar a
 * rota. `catch` do Zod é o que faz isso sem `try`.
 */
const searchSchema = z.object({
  status: z.enum(TRANSACTION_STATUSES).optional().catch(undefined),
  from: z.string().regex(ISO_DATE).optional().catch(undefined),
  to: z.string().regex(ISO_DATE).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  // `seller_id` é `zId('sel')` na API: um `sel_…` malformado voltaria 400 e derrubaria a
  // lista inteira por causa de um link torto.
  seller_id: z.string().regex(SELLER_ID).optional().catch(undefined),
  /** Modo da leitura admin; `live` é o default da API e não vai para a URL. */
  mode: z.enum(['live', 'test']).optional().catch(undefined),
  /** `brl` (default, fora da URL) ou `global`: aba do topo da tela. */
  region: regionSearchField,
});

/** Período default (30 dias) quando a URL não traz `from`/`to`. */
const DEFAULT_PRESET = '30d' as const;

function AdminTransactions() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  // A rota `$id` é filha desta: se ela casa, o detalhe está aberto por cima da lista.
  const detail = useMatch({ from: '/admin/transactions/$id', shouldThrow: false });
  const header = useAdminTransactionDrawerHeader(detail?.params.id, search.mode ?? 'live');

  const fallback = rangeForPreset(DEFAULT_PRESET);
  const value: AdminFilterValue = {
    status: search.status,
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
    q: search.q,
    sellerId: search.seller_id,
    mode: search.mode ?? 'live',
    region: regionOf(search.region),
  };

  // "Vazio" e "filtro sem resultado" são estados diferentes: o período default não conta
  // como filtro, o que o operador mexeu conta. `mode` também NÃO conta: ele escolhe o
  // conjunto de dados, não recorta um. Em `test` sem cobrança o vazio certo é "nenhum
  // seller recebeu uma cobrança neste modo ainda", não "troque o status".
  const filtered =
    value.status !== undefined ||
    value.q !== undefined ||
    value.sellerId !== undefined ||
    value.from !== fallback.from ||
    value.to !== fallback.to;

  const apply = (next: AdminFilterValue): void => {
    void navigate({
      to: '/admin/transactions',
      search: {
        ...(next.status === undefined ? {} : { status: next.status }),
        ...(next.from === fallback.from ? {} : { from: next.from }),
        ...(next.to === fallback.to ? {} : { to: next.to }),
        ...(next.q === undefined ? {} : { q: next.q }),
        ...(next.sellerId === undefined ? {} : { seller_id: next.sellerId }),
        ...(next.mode === 'live' ? {} : { mode: next.mode }),
        ...regionSearchOf(next.region),
      },
      replace: true,
    });
  };

  return (
    <>
      <AdminTransactionsScreen
        value={value}
        onChange={apply}
        filtered={filtered}
        {...(detail === undefined ? { selectedId: undefined } : { selectedId: detail.params.id })}
        onSelect={(row) => {
          // `search` explícito: o filtro da lista tem de sobreviver à abertura do drawer,
          // e o `$id` herda o schema desta rota.
          void navigate({ to: '/admin/transactions/$id', params: { id: row.id }, search });
        }}
        onClearFilters={() => {
          // "Limpar filtros" NÃO troca o modo: mudar de `test` para `live` sem pedir
          // trocaria o conjunto de dados debaixo do operador.
          void navigate({
            to: '/admin/transactions',
            search: {
              ...(value.mode === 'live' ? {} : { mode: value.mode }),
              ...regionSearchOf(value.region),
            },
            replace: true,
          });
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/admin/transactions', search });
        }}
        title={header.title}
        {...(header.sub === undefined ? {} : { sub: header.sub })}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/transactions')({
  validateSearch: searchSchema,
  component: AdminTransactions,
});
